import ServiceCardScene from "@/components/ServiceCardScene";
import { ArrowUpRight } from "lucide-react";
import { useEffect, useRef } from "react";

const services = [
  {
    id: "web",
    no: "01",
    title: "Web Development",
    sub: "Modern websites built for growth.",
    desc: "Responsive business websites, landing pages, and custom web platforms designed to improve branding, customer engagement, and online presence. Corporate sites, e-commerce stores, dashboards and client portals.",
    tags: ["Corporate", "Landing", "Portfolio", "E-Commerce"],
    kind: "globe",
    color: "#ff4500",
    span: "md:col-span-8",
    minH: "min-h-[520px]",
  },
  {
    id: "saas",
    no: "02",
    title: "Lightweight SaaS Solutions",
    sub: "Custom systems for modern businesses.",
    desc: "From web applications to mobile apps and lightweight SaaS platforms, we develop scalable solutions that streamline operations and improve productivity.",
    tags: ["WaaS", "Dashboards", "MVP", "API"],
    kind: "cube",
    color: "#ccff00",
    span: "md:col-span-4",
    minH: "min-h-[520px]",
  },
  {
    id: "ai",
    no: "03",
    title: "AI & Automation",
    sub: "Smarter technology for efficiency.",
    desc: "Integrate AI-powered tools, automation workflows, and intelligent digital solutions that optimize processes and elevate customer experiences.",
    tags: ["AI Chatbots", "Workflow Automation", "Integrations"],
    kind: "pyramid",
    color: "#ff4500",
    span: "md:col-span-6",
    minH: "min-h-[420px]",
  },
  {
    id: "consulting",
    no: "04",
    title: "Digital & IT Consulting",
    sub: "Expert guidance to build smarter.",
    desc: "We help businesses make informed technology decisions — from technical discovery and architecture planning to vendor evaluation and roadmap strategy. Our consulting ensures you invest in the right solutions, avoid costly mistakes, and move forward with clarity.",
    tags: ["Discovery", "Architecture", "Vendor eval", "Roadmap"],
    kind: "globe",
    color: "#ccff00",
    span: "md:col-span-6",
    minH: "min-h-[420px]",
    cta: true,
  },
];

export default function Services() {
  const root = useRef(null);

  useEffect(() => {
    const els = root.current?.querySelectorAll(".reveal");
    if (!els) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("is-in");
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const goToContact = () => {
    const el = document.getElementById("contact");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -40 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="services"
      data-testid="section-services"
      ref={root}
      className="relative py-28 md:py-40 bg-[var(--bg)]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-6 reveal">
              <span className="w-2 h-2 bg-[var(--vermilion)] rounded-full" />
              <span className="overline">03 — What we build</span>
            </div>
            <h2
              data-testid="services-headline"
              className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.95] max-w-3xl reveal"
            >
              Smart digital solutions,<br />
              <span className="text-[var(--lime)]">built end-to-end.</span>
            </h2>
          </div>
          <p className="text-[var(--text-2)] max-w-sm reveal">
            Four pillars. One studio. From the first wireframe to the live release —
            we own the entire build.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {services.map((s) => (
            <article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              className={`${s.span} ${s.minH} group relative overflow-hidden rounded-sm border border-[var(--border-dim)] bg-[var(--bg-2)] hover:border-[var(--text-3)] transition-all duration-500 reveal`}
            >
              {/* 3D backdrop */}
              <div className="absolute inset-0 opacity-60 group-hover:opacity-100 transition-opacity duration-700">
                <ServiceCardScene kind={s.kind} color={s.color} />
              </div>
              {/* Gradient mask */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.0) 30%, var(--bg-2) 100%)",
                }}
              />

              <div className="relative h-full p-7 md:p-9 flex flex-col justify-between">
                <div className="flex items-start justify-between">
                  <span className="font-mono-ui text-[11px] tracking-[0.25em] text-[var(--text-3)]">
                    {s.no} / SERVICE
                  </span>
                  <ArrowUpRight className="w-5 h-5 text-[var(--text-3)] group-hover:text-[var(--lime)] group-hover:rotate-45 transition-all duration-500" />
                </div>

                <div>
                  <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                    {s.title}
                  </h3>
                  {s.sub && (
                    <div className="mt-1 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--lime)]">
                      {s.sub}
                    </div>
                  )}
                  <p className="mt-3 text-[var(--text-2)] max-w-md leading-relaxed text-sm md:text-base">
                    {s.desc}
                  </p>

                  <div className="mt-6 flex flex-wrap gap-2">
                    {s.tags.map((t) => (
                      <span
                        key={t}
                        className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-2)] border border-[var(--border-dim)] px-2.5 py-1 rounded-full bg-[var(--bg)]/30"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  {s.cta && (
                    <button
                      data-testid={`service-cta-${s.id}`}
                      onClick={goToContact}
                      className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[var(--vermilion)] hover:bg-[var(--vermilion-2)] text-white text-xs tracking-wide rounded-full transition-colors"
                    >
                      Start a project <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
